// Portfolio d'une exposition (ARTO-197) : le dossier d'œuvres en PDF
// (`documents-prestige`) ou une salle privée à lien révocable
// (`viewing-rooms`), préremplis des œuvres de l'accrochage. Chaque entrée
// suit son module ; le serveur les garde de toute façon.
import { Button, DropdownMenu, type DropdownMenuEntry } from '@artosera/ui';
import { OEUVRES_PAR_SALLE_MAX } from '@artosera/shared';
import { ChevronDown, Share2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OeuvreDeLExposition } from '../../api/expositions.js';
import { galerieALaction, galerieEcritModule } from '../../auth/sujet.js';
import { DossierOeuvresDialog } from '../oeuvres/DossierOeuvresDialog.js';
import { useShellContext } from '../shell/context.js';
import { NouvelleSalleDialog } from '../viewing-rooms/NouvelleSalleDialog.js';

const OEUVRES_PAR_DOSSIER_MAX = 30;

export function PortfolioExposition({
  titre,
  oeuvres,
}: {
  titre: string;
  oeuvres: readonly OeuvreDeLExposition[];
}) {
  const { t } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const [ouvert, setOuvert] = useState<'dossier' | 'salle' | null>(null);
  // Une œuvre archivée ne part jamais dans un portfolio.
  const retenues = oeuvres.filter((oeuvre) => oeuvre.statut !== 'archivee');
  const peutDossier =
    galerieEcritModule(galerie, 'documents-prestige') &&
    galerieALaction(galerie, 'documents:write');
  const peutSalle =
    galerieEcritModule(galerie, 'viewing-rooms') && galerieALaction(galerie, 'viewing-rooms:write');
  if (retenues.length === 0 || (!peutDossier && !peutSalle)) return null;

  const libelle = (cle: 'dossier' | 'salle', max: number) =>
    retenues.length > max
      ? `${t(`expositions.portfolio.${cle}`)} · ${t('expositions.portfolio.limite', { count: max })}`
      : t(`expositions.portfolio.${cle}`);
  const entrees: DropdownMenuEntry[] = [
    ...(peutDossier
      ? [
          {
            type: 'item' as const,
            item: {
              key: 'dossier',
              label: libelle('dossier', OEUVRES_PAR_DOSSIER_MAX),
              onSelect: () => setOuvert('dossier'),
            },
          },
        ]
      : []),
    ...(peutSalle
      ? [
          {
            type: 'item' as const,
            item: {
              key: 'salle',
              label: libelle('salle', OEUVRES_PAR_SALLE_MAX),
              onSelect: () => setOuvert('salle'),
            },
          },
        ]
      : []),
  ];
  return (
    <>
      <DropdownMenu
        align="start"
        entries={entrees}
        trigger={
          <Button variant="secondary" size="sm">
            <Share2 size={16} strokeWidth={1.5} aria-hidden />
            {t('expositions.portfolio.action')}
            <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
          </Button>
        }
      />
      {ouvert === 'dossier' && (
        <DossierOeuvresDialog
          open
          onOpenChange={(o) => setOuvert(o ? 'dossier' : null)}
          titreInitial={titre}
          oeuvreIds={retenues.slice(0, OEUVRES_PAR_DOSSIER_MAX).map((oeuvre) => oeuvre.id)}
        />
      )}
      {ouvert === 'salle' && (
        <NouvelleSalleDialog
          galerieSlug={galerieSlug}
          ouvert
          onFermer={() => setOuvert(null)}
          titreInitial={titre}
          oeuvresInitiales={retenues
            .slice(0, OEUVRES_PAR_SALLE_MAX)
            .map((oeuvre) => ({ id: oeuvre.id, titre: oeuvre.titre }))}
        />
      )}
    </>
  );
}
